import { useMemo, useRef } from 'react';

import type { ExecutionSearchSummary } from '@src/models/interfaces/execution.interface';
import { ScheduleType } from '@src/models/interfaces/schedule.interface';
import { useElasticDivisionIds } from '@src/packages/utils';
import { ALLOWED_ROLES } from '@src/packages/utils/access';
import useGetDivisionsAllowed from '@src/packages/utils/hooks/useGetDivisionsAllowed';
import ExecutionService from '@src/services/ExecutionService';
import ScheduleService from '@src/services/ScheduleService';

import { resolveSearchDivisionIds } from '../utils/searchDivisionIds';
import useDebouncedRemoteSearch from './useDebouncedRemoteSearch';

const SEARCH_PAGE_SIZE = 8;
const TASK_SCHEDULE_LOOKUP_SIZE = 50;

interface UseSidebarTaskSearchParams {
  enabled: boolean;
  orgId?: string;
  searchTerm: string;
}

const useSidebarTaskSearch = ({ enabled, orgId, searchTerm }: UseSidebarTaskSearchParams) => {
  const servicesRef = useRef<{ executions: ExecutionService; schedules: ScheduleService } | null>(
    null,
  );
  if (servicesRef.current === null) {
    servicesRef.current = { executions: new ExecutionService(), schedules: new ScheduleService() };
  }

  const { allowedDivsIds, allowedSelectedDivs } = useGetDivisionsAllowed(
    ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.WORK_ORDER.LIST,
  );
  const elasticDivIds = useElasticDivisionIds();
  const divisionIds = useMemo(
    () => resolveSearchDivisionIds(elasticDivIds, allowedDivsIds, allowedSelectedDivs),
    [elasticDivIds, allowedDivsIds, allowedSelectedDivs],
  );

  const { results, loading, error } = useDebouncedRemoteSearch<ExecutionSearchSummary>({
    enabled: enabled && Boolean(orgId) && divisionIds !== null,
    searchTerm,
    scopeKey: `${orgId}|${divisionIds?.join(',') ?? ''}`,
    search: async normalizedTerm => {
      if (!orgId || !divisionIds || !servicesRef.current) return [];
      const { executions, schedules } = servicesRef.current;

      // Executions do not index their schedule type: resolve matching task schedules first.
      const taskSchedules = await schedules.searchSummaries({
        orgId,
        divsIds: divisionIds,
        searchTerm: normalizedTerm,
        types: [ScheduleType.Task],
        pageSize: TASK_SCHEDULE_LOOKUP_SIZE,
      });
      if (!taskSchedules.length) return [];

      return executions.searchSummaries({
        orgId,
        divsIds: divisionIds,
        scheduleIds: taskSchedules.map(schedule => schedule.id),
        pageSize: SEARCH_PAGE_SIZE,
      });
    },
  });

  return { tasks: results, loading, error };
};

export default useSidebarTaskSearch;
